import { Order, OrderPaymentStatus, OrderStatus } from '@favid-inc/api';
import { OrderByElement, WhereElement } from '@favid-inc/api/lib/rest';
import { Filter } from 'core/generic';
import { Paginate } from 'core/paginate';
import { menuRoutes } from 'features/menu';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { EmptyList, Loading } from 'utils';
import { FilterComponent } from './list/Filter';

const entries = [
  { key: 'id', label: 'ID' },
  { key: 'artistArtisticName', label: 'Artista' },
  { key: 'customerName', label: 'Cliente' },
  { key: 'status', label: 'Status' },
  { key: 'paymentStatus', label: 'Pagamento' },
  { key: 'statusPlacedDate', label: 'Data pedido' },
  { key: 'statusDeclinedDate', label: 'Data negação pedido' },
  { key: 'statusFulfilledDate', label: 'Data envio pedido' },
  { key: 'iuguInvoiceMetadata', label: 'Fatura' },
];

const icons: IconChecker[] = [
  {
    match: OrderStatus.DECLINED,
    iconClass: 'icon thumbs down outline red',
    description: 'Recusado pelo artista',
  },
  {
    match: OrderStatus.CANCELED,
    iconClass: 'icon clock outline yellow',
    description: 'Cancelado',
  },
  {
    match: OrderStatus.FULFILLED,
    iconClass: 'icon thumbs up outline green',
    description: 'Finalizado',
  },
  {
    match: OrderStatus.PLACED,
    iconClass: 'icon clock outline blue',
    description: 'Enviado',
  },
  {
    match: OrderPaymentStatus.AUTHORIZED,
    iconClass: 'icon dollar sign blue',
    description: 'Autorizado',
  },
  {
    match: OrderPaymentStatus.REFUSED,
    iconClass: 'icon dollar sign red',
    description: 'Recusadp',
  },
  {
    match: OrderPaymentStatus.REFUNDED,
    iconClass: 'icon dollar sign teal',
    description: 'Estorno',
  },
  {
    match: OrderPaymentStatus.PENDING_REFUND,
    iconClass: 'icon dollar sign blue',
    description: 'Estorno pendente',
  },
  {
    match: OrderPaymentStatus.PAID,
    iconClass: 'icon dollar sign green',
    description: 'Pago',
  },
  {
    match: OrderPaymentStatus.WAITING_PAYMENT,
    iconClass: 'icon dollar sign yellow',
    description: 'Aguardando pagamento',
  },
];

const EmptyTable = () => {
  return (
    <tr>
      <td colSpan={entries.length}>
        <EmptyList message='Não existem pedidos.' />
      </td>
    </tr>
  );
};

interface IconChecker {
  match: string;
  description: string;
  iconClass: string;
}

const getIcon = (status?: string) => {
  const index = _.findIndex(icons, { match: status });
  if (index >= 0) {
    return icons[index];
  }
};

const TdData = (params: { k: string; value: any; artistId?: number }) => {
  let { value } = params;
  const { k, artistId } = params;
  const icon = getIcon(value);

  if (icon) {
    return (
      <td>
        <i className={icon.iconClass} />
        {icon.description}
      </td>
    );
  }

  if (k === 'artistArtisticName' && artistId) {
    return (
      <td>
        <Link to={`${menuRoutes.ARTISTAS}/${artistId}`}>{value}</Link>
      </td>
    );
  }

  const dateCells = [
    'statusPlacedDate',
    'statusDeclinedDate',
    'statusFulfilledDate',
  ];
  if (value && dateCells.includes(k)) {
    value = moment(value).format('l');
  }

  if (!value || typeof value !== 'object') {
    return <td>{value}</td>;
  }

  if (value.url) {
    return (
      <td>
        <a target='_blank' rel='noopener noreferrer' href={value.url}>
          Fatura
        </a>
      </td>
    );
  }

  return <td />;
};

const Td = (params: { order: Order }) => {
  const { order } = params;

  const tds = entries.map(({ key }) => {
    return Object.entries(order).map(([k, value]) => {
      if (key === k) {
        return <TdData artistId={order.artistId} key={k} k={k} value={value} />;
      }
      return null;
    });
  });

  return <React.Fragment>{tds}</React.Fragment>;
};

const TableBody = (params: { orders: Order[] }) => {
  const { orders } = params;
  if (orders.length === 0) {
    return <EmptyTable />;
  }

  const trs = orders.map((order, i) => {
    return (
      <tr key={`order_${i}`}>
        <Td order={order} />
      </tr>
    );
  });

  return <React.Fragment>{trs}</React.Fragment>;
};

interface Props {
  orders: Order[];
  total: number;
  filter: Filter;
  orderBy: OrderByElement;
  isLoading: boolean;
  isArtistSearchDisable?: boolean;
  onFilterChange: (filter: WhereElement[]) => void;
  onPaginateChange: (selectedItem: { selected: number }) => void;
  onChangeSort: (orderBy: OrderByElement) => void;
}

class ArtistOrdersTableComponent extends React.Component<Props> {
  onChangeOrdenation = (event: React.SyntheticEvent<HTMLElement>) => {
    const attr = event.currentTarget.dataset.value || '';
    const { exp: order, attr: current } = this.props.orderBy;
    const exp =
      current === attr && order === 'ASC'
        ? ('DESC' as 'DESC')
        : ('ASC' as 'ASC');

    this.props.onChangeSort({
      attr,
      exp,
    });
  };

  renderTable() {
    const { onPaginateChange, total, filter, isLoading } = this.props;
    const { page, itensPerPage } = filter;

    if (isLoading) {
      return <Loading />;
    }

    const ths = entries.map(({ key, label }) => {
      const { attr, exp } = this.props.orderBy;
      const isASC = exp === 'ASC';
      const isCurrentSort = key === attr;
      const icon = isCurrentSort && isASC ? 'fa-sort-asc' : 'fa-sort-desc';
      const style = {
        marginRight: '.5em',
        color: isCurrentSort ? 'black' : 'gray',
      };

      return (
        <th onClick={this.onChangeOrdenation} data-value={key} key={key}>
          <i className={`fa ${icon}`} style={style} />
          {label}
        </th>
      );
    });

    return (
      <div>
        <table className='ui celled table'>
          <thead>
            <tr>{ths}</tr>
          </thead>
          <tbody>
            <TableBody orders={this.props.orders} />
          </tbody>
        </table>
        <Paginate
          page={page}
          total={total}
          itensPerPage={itensPerPage}
          onPaginateChange={onPaginateChange}
        />
      </div>
    );
  }

  render() {
    const { onFilterChange, filter, isArtistSearchDisable } = this.props;

    return (
      <div>
        <div>
          <FilterComponent
            filter={filter}
            onFilterChange={onFilterChange}
            isArtistSearchDisable={isArtistSearchDisable}
          />
        </div>
        {this.renderTable()}
      </div>
    );
  }
}

export const ArtistOrdersTable = ArtistOrdersTableComponent;
